import React, { useState, useRef, ChangeEvent, KeyboardEvent, useEffect, useCallback } from 'react';

interface MpinInputProps {
  pinLength: number;
  enabled?: boolean;
  onChange: (pin: string) => void;
  onFinish: (pin: string) => void;
  label?: string;
  verifyMode?: boolean; // New prop to enable verification
  onVerificationMismatch?: () => void; // Callback for when verification fails
  onVerificationSuccess?: () => void; // Callback for when verification succeeds
  onVerificationStepChanged?: (step:number) => void;
}

const MpinInput: React.FC<MpinInputProps> = ({
  enabled = true,
  pinLength,
  onChange,
  onFinish,
  label = "MPIN",
  verifyMode = false,
  onVerificationMismatch,
  onVerificationSuccess,
  onVerificationStepChanged,
}) => {
  const [pinDigits, setPinDigits] = useState<string[]>(Array(pinLength).fill(''));
  const [firstPin, setFirstPin] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const focusFirstInput = useCallback(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  useEffect(() => {
    focusFirstInput();
  }, [pinLength, isVerifying, focusFirstInput]); // Re-focus when pinLength or verification state changes

  const resetInput = useCallback(() => {
    setPinDigits(Array(pinLength).fill(''));
    focusFirstInput();
  }, [pinLength, focusFirstInput]);

  const handleComplete = useCallback((currentPin: string) => {
    if (verifyMode) {
      if (!isVerifying) {
        // First PIN entered
        setFirstPin(currentPin);
        setIsVerifying(true);
        onChange(''); // Clear the external state for the next input
        resetInput(); // Clear inputs for verification
        if (onVerificationStepChanged) onVerificationStepChanged(1)
      } else {
        // Verification PIN entered
        if (firstPin === currentPin) {
          onFinish(currentPin);
          onVerificationSuccess?.();
          // Optionally reset or keep the pin for display
        } else {
          onVerificationMismatch?.();
          resetInput(); // Clear inputs on mismatch for re-entry
        }
        //setFirstPin(null); // Reset firstPin for next cycle
        //setIsVerifying(false); // Exit verification mode
        //if (onVerificationStepChanged) onVerificationStepChanged(2)
      }
    } else {
      // Not in verify mode, just finish
      onFinish(currentPin);
      setTimeout(() => {
        resetInput(); // Clear inputs after finishing
      }, 1000)
    }
  }, [verifyMode, isVerifying, firstPin, onFinish, onVerificationMismatch, onVerificationSuccess, onChange, resetInput]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>, index: number) => {
    const { value } = event.target;
    const newPinDigits = [...pinDigits];

    // Only allow a single numeric digit
    if (/^[0-9]$/.test(value)) {
      newPinDigits[index] = value;
      setPinDigits(newPinDigits);

      // Move focus to the next input if available
      if (index < pinLength - 1 && inputRefs.current[index + 1]) {
        inputRefs.current[index + 1]?.focus();
      }
    } else if (value === '') {
      // Allow clearing the current input
      newPinDigits[index] = '';
      setPinDigits(newPinDigits);
    }

    const currentPin = newPinDigits.join('');
    onChange(currentPin); // Always update external onChange

    // If all digits are entered, call handleComplete
    if (currentPin.length === pinLength && newPinDigits.every(digit => digit !== '')) {
      handleComplete(currentPin);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (event.key === 'Backspace' && pinDigits[index] === '') {
      // If backspace is pressed on an empty input, move focus to the previous input
      if (index > 0 && inputRefs.current[index - 1]) {
        inputRefs.current[index - 1]?.focus();
      }
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pasteData = event.clipboardData.getData('text').trim();
    if (/^[0-9]*$/.test(pasteData) && pasteData.length <= pinLength) {
      const newPinDigits = Array(pinLength).fill('');
      for (let i = 0; i < pasteData.length; i++) {
        newPinDigits[i] = pasteData[i];
      }
      setPinDigits(newPinDigits);
      onChange(newPinDigits.join(''));

      // Move focus to the last filled input or the last input if all are filled
      const lastFilledIndex = Math.min(pasteData.length - 1, pinLength - 1);
      if (inputRefs.current[lastFilledIndex]) {
        inputRefs.current[lastFilledIndex]?.focus();
      }

      if (pasteData.length === pinLength) {
        handleComplete(newPinDigits.join(''));
      }
    }
  };

  const currentLabel = isVerifying ? "Verify MPIN" : label;

  return (
    <div className="mpin-input-container">
      {/* {currentLabel && <label>{currentLabel}</label>} */}
      <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
        {pinDigits.map((digit, index) => (
          <input
            key={index}
            disabled={!enabled}
            type="password" // Masks the input
            maxLength={1} // Each input takes only one digit
            value={digit}
            onChange={(e) => handleChange(e, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onPaste={handlePaste}
            ref={(el) => (inputRefs.current[index] = el)}
            inputMode="numeric"
            pattern="[0-9]"
            style={{
              width: '40px',
              height: '40px',
              fontSize: '24px',
              textAlign: 'center',
              border: '1px solid #ccc',
              borderRadius: '4px',
              boxSizing: 'border-box',
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default MpinInput;
